import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowLeft } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import PackageStepIndicator from '@/components/PackageStepIndicator';
import SignatureCanvas from 'react-signature-canvas';
import { getPackage, getStepPath, getLevelStyles } from '@/data/packageData';
import { usePackage } from '@/context/PackageContext';

const LOGO_URL = '/images/TCA_Logo.png';
const BANNER_URL = '/images/TCA_Banner.png';
const TCA_SHORT_LOGO = '/images/TCA_Short_Logo.png';
const SARS_LOGO_URL = '/images/SARS_Logo.png';

const PowerOfAttorney = () => {
  const { packageSlug } = useParams();
  const navigate = useNavigate();
  const pkg = getPackage(packageSlug);
  const { clientData, updateClientData } = usePackage();
  const data = clientData || {};

  const [signedAt, setSignedAt] = useState(data?.signedAtPOA || '');
  const [signature, setSignature] = useState(data?.signaturePOA || null);
  const [locationError, setLocationError] = useState('');
  const [errorModal, setErrorModal] = useState({ open: false, message: '' });
  const signatureRef = useRef(null);

  useEffect(() => {
    if (!pkg) navigate('/', { replace: true });
    window.scrollTo(0, 0);
  }, [pkg, navigate]);

  // Restore previously-saved signature into the canvas (if returning to this page)
  useEffect(() => {
    if (signature && signatureRef.current && typeof signatureRef.current.fromDataURL === 'function') {
      try { signatureRef.current.fromDataURL(signature); } catch (_) { /* ignore */ }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-persist any changes to location / signature so values survive back-navigation
  useEffect(() => {
    updateClientData({ ...data, signedAtPOA: signedAt, signaturePOA: signature });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signedAt, signature]);

  if (!pkg) return null;

  // Today's date pieces
  const today = new Date();
  const dayOfMonth = today.getDate();
  const monthYear = today.toLocaleDateString('en-ZA', { month: 'long', year: 'numeric' });

  // Pull the client's identifying details captured on Personal Details page
  const fullName = [data.firstName, data.secondName, data.middleName, data.surname]
    .filter(Boolean)
    .join(' ') || '[Client Full Name]';
  const idNumber = data.idNumber || '[ID Number]';
  const taxNumber = data.taxNumber || '[Tax Number]';
  const companyName = data.companyName || '[Company Name]';
  const vatNumber = (data.vatNumber || '').trim();

  const validateLocation = (value) => {
    if (!value || value.trim() === '') return '';
    const locationRegex = /^[a-zA-Z\s]+$/;
    if (!locationRegex.test(value)) return 'Location can only contain letters (no numbers or special characters)';
    return '';
  };

  const handleLocationChange = (e) => {
    const value = e.target.value;
    setSignedAt(value);
    setLocationError(validateLocation(value));
  };

  const handleEndStroke = () => {
    if (signatureRef.current && !signatureRef.current.isEmpty()) {
      setSignature(signatureRef.current.toDataURL('image/png'));
    }
  };

  const clearSignature = () => {
    signatureRef.current?.clear();
    setSignature(null);
  };

  const isFormValid = () => signedAt && signedAt.trim() !== '' && !!signature && !locationError;

  const handleSubmit = () => {
    if (!signedAt || signedAt.trim() === '') {
      setErrorModal({ open: true, message: 'Please enter the location where you are signing.' });
      return;
    }
    const err = validateLocation(signedAt);
    if (err) {
      setLocationError(err);
      setErrorModal({ open: true, message: err });
      return;
    }
    if (!signature || (signatureRef.current && signatureRef.current.isEmpty())) {
      setErrorModal({ open: true, message: 'Please provide your signature before proceeding.' });
      return;
    }
    updateClientData({ ...data, signedAtPOA: signedAt.trim(), signaturePOA: signature });
    navigate(getStepPath(packageSlug, 'popi'));
  };

  const levelStyles = getLevelStyles(pkg.colorKey);

  // Reusable inline blue-highlight pill (matches the source PageTwo styling)
  const InlinePill = ({ children, testid }) => (
    <span
      data-testid={testid}
      className="inline-block bg-blue-50 text-tca-blue font-bold rounded px-2 py-0.5 mx-0.5 border-b-2 border-tca-blue"
    >
      {children}
    </span>
  );

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <main className="flex-1 flex flex-col items-center px-4 pt-[2px] pb-8 sm:pb-12 bg-white">
        {/* Logo */}
        <div className="mb-6 sm:mb-8 flex flex-col items-center gap-4">
          <img src={LOGO_URL} alt="TCA Auditors Logo" className="h-24 sm:h-32 w-auto object-contain select-none" draggable="false" />
          <img src={BANNER_URL} alt="Tax & Auditing Consultants - For Businesses & Individuals" className="h-[4.5rem] sm:h-24 w-auto object-contain" />
        </div>

        {/* Heading */}
        <div className="mb-4 pb-2">
          <h1 className="text-3xl sm:text-4xl font-extrabold text-tca-blue text-center tracking-wide inline-block underline-tca-blue">
            ACCOUNTING RETAINER AGREEMENT
          </h1>
        </div>

        {/* Top back button */}
        <div className="w-full max-w-3xl flex justify-start mb-6">
          <Button
            onClick={() => navigate(getStepPath(packageSlug, 'details'))}
            className="bg-accent hover:bg-accent/90 text-white font-semibold py-2 px-6 h-9 text-sm rounded-lg shadow-soft hover:shadow-card transition-all duration-200 flex items-center gap-2"
            data-testid="back-to-details-top"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Personal Details
          </Button>
        </div>

        <Card className="w-full max-w-3xl border-0 shadow-card">
          <CardContent className="p-6 sm:p-10 space-y-6">
            {/* Package badge */}
            <div className="flex justify-center">
              <span className="text-sm font-bold px-4 py-1.5 rounded-full" style={levelStyles} data-testid="package-badge">
                {pkg.fullName}
              </span>
            </div>

            {/* SARS Header — matches the official SARS Power of Attorney layout:
                logo on the LEFT, two-line title block to its right. */}
            <div className="flex items-start gap-5 sm:gap-6 pt-2">
              <img
                src={SARS_LOGO_URL}
                alt="SARS Logo"
                className="h-20 sm:h-24 w-auto object-contain flex-shrink-0"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
              <div className="flex flex-col justify-center pt-1">
                <p
                  className="font-bold text-[15pt] leading-tight text-foreground underline decoration-1 underline-offset-2"
                  style={{ fontFamily: 'Arial, Helvetica, sans-serif' }}
                >
                  SOUTH AFRICAN REVENUE SERVICE
                </p>
                <p
                  className="text-[12pt] leading-tight text-foreground mt-1"
                  style={{ fontFamily: 'Arial, Helvetica, sans-serif' }}
                >
                  Special Power of Attorney to Tax Practitioner
                </p>
              </div>
            </div>

            <div className="border-t border-border" />

            {/* Document body */}
            <div className="space-y-4 text-sm sm:text-base text-foreground leading-relaxed">
              <p className="font-semibold">
                The Commissioner for the South African Revenue Service
              </p>

              <p>
                I, the undersigned, <InlinePill testid="poa-fullname">{fullName}</InlinePill>{' '}
                Identity/Pass. Number: <InlinePill testid="poa-idnumber">{idNumber}</InlinePill>
              </p>

              <p>
                in my capacity as the representative taxpayer of{' '}
                <InlinePill testid="poa-companyname">{companyName}</InlinePill> hereby appoint{' '}
                <strong>Schalk P.B. Coetzee</strong>, Identity/Passport Number:{' '}
                <strong>8811205075080</strong> of Tax Practitioner Firm <strong>TC Auditors</strong>, to act on behalf of{' '}
                <InlinePill testid="poa-companyname-2">{companyName}</InlinePill> in respect of the following tax matter(s) detailed below:
              </p>

              <div className="space-y-3">
                <p className="font-bold">
                  1. INCOME TAX REFERENCE: <InlinePill testid="poa-taxnumber">{taxNumber}</InlinePill>
                </p>
                {vatNumber && (
                  <p className="font-bold">
                    2. VAT REFERENCE: <InlinePill testid="poa-vatnumber">{vatNumber}</InlinePill>
                  </p>
                )}
                <ul className="list-disc pl-6 space-y-1.5">
                  <li>To communicate to SARS any change of registered particulars</li>
                  <li>To request and/or follow up on the issuing of tax clearance certificates</li>
                  <li>To complete and/or submit returns to SARS</li>
                  <li>To communicate with SARS and to submit relevant material to SARS</li>
                  <li>To lodge and pursue an objection against an assessment raised or decision made by SARS</li>
                  <li>To file and pursue an appeal against an assessment raised or decision made by SARS</li>
                </ul>
              </div>

              <p>
                I confirm that the authority that has been delegated to <strong>Schalk P.B. Coetzee</strong> by this
                Special Power of Attorney may be performed by a person who is under his or her direct supervision.
              </p>

              <p>
                I confirm, for the purpose of absolute clarity that anything done by <strong>Schalk P.B. Coetzee</strong>{' '}
                or person under his/her direct supervision pursuant to this Special Power of Attorney shall be regarded,
                for all intents and purposes, as having been done by myself and I undertake to ratify any actions taken
                in terms of this Special Power of Attorney.
              </p>

              <p>
                This Special Power of Attorney shall operate for a period of <strong>24 month(s)</strong> from the date
                of my signature.
              </p>
            </div>

            {/* Date / Location */}
            <div className="space-y-2 pt-2">
              <p className="text-sm sm:text-base text-foreground flex flex-wrap items-center gap-2">
                <span>Signed at</span>
                <input
                  type="text"
                  value={signedAt}
                  onChange={handleLocationChange}
                  onBlur={(e) => setSignedAt(e.target.value.trim())}
                  placeholder="Enter location"
                  className={`bg-blue-50 px-2 py-1 w-44 focus:outline-none font-bold text-tca-blue rounded border-b-2 ${
                    locationError ? 'border-destructive' : 'border-tca-blue'
                  }`}
                  data-testid="poa-location-input"
                />
                <span>on this</span>
                <span className="font-bold">{dayOfMonth}</span>
                <span>day of</span>
                <span className="font-bold">{monthYear}</span>
              </p>
              {locationError && (
                <p className="text-sm text-destructive" data-testid="poa-location-error">{locationError}</p>
              )}
            </div>

            {/* Signature */}
            <div className="form-section signature-section pt-2">
              <p className="signature-instruction">Please draw your signature in the box below:</p>

              <div className="signature-container">
                <SignatureCanvas
                  ref={signatureRef}
                  onEnd={handleEndStroke}
                  canvasProps={{
                    className: 'signature-canvas',
                    'data-testid': 'signature-canvas',
                  }}
                />
              </div>

              <button
                data-testid="button-clear-signature"
                type="button"
                onClick={clearSignature}
                className="clear-signature-btn no-print"
              >
                Clear Signature
              </button>
            </div>

            {/* Signature label + line — sits OUTSIDE the canvas wrapper, left-aligned.
                Label and line on the same row (line to the right of the word, sitting on
                the baseline of the row). */}
            <div className="pt-4 flex items-end gap-3 flex-wrap">
              <span className="text-sm sm:text-base font-semibold text-foreground">Signature:</span>
              <span className="inline-block w-[253px] border-b border-gray-400" />
            </div>

            {/* Witnesses */}
            <div className="pt-4 space-y-20">
              <p className="text-sm sm:text-base font-semibold text-foreground">As witnesses:</p>

              <div className="flex items-end gap-3 flex-wrap">
                <span className="font-semibold">1.</span>
                <span className="inline-block w-[253px] border-b border-gray-400" />
                <span className="text-sm">
                  Full Name: <strong>Ruan England</strong>
                </span>
              </div>

              <div className="flex items-end gap-3 flex-wrap">
                <span className="font-semibold">2.</span>
                <span className="inline-block w-[253px] border-b border-gray-400" />
                <span className="text-sm">
                  Full Name: <strong>Anel Barwise</strong>
                </span>
              </div>
            </div>

            {/* Footer / Version metadata */}
            <div className="border-t border-border pt-4 text-xs text-muted-foreground flex flex-wrap justify-between gap-2">
              <span>Version: 2016.01.00</span>
              <span>Form ID: TPPOA</span>
              <span>Page of Page: 01/01</span>
            </div>

            {/* Action button */}
            <div className="pt-4 flex justify-center">
              <Button
                type="button"
                onClick={handleSubmit}
                disabled={!isFormValid()}
                data-testid="btn-next-page"
                className="w-full sm:w-auto bg-accent hover:bg-accent/90 text-accent-foreground font-semibold py-3 px-8 h-12 text-base rounded-lg shadow-soft hover:shadow-card transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Go to the Next Page &rarr;
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Bottom back button */}
        <div className="w-full max-w-3xl flex justify-start mt-4 mb-6">
          <Button
            onClick={() => navigate(getStepPath(packageSlug, 'details'))}
            className="bg-accent hover:bg-accent/90 text-white font-semibold py-2 px-6 h-9 text-sm rounded-lg shadow-soft hover:shadow-card transition-all duration-200 flex items-center gap-2"
            data-testid="back-to-details-bottom"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Personal Details
          </Button>
        </div>
      </main>

      <div className="relative">
        <PackageStepIndicator pkg={pkg} currentStepKey="poa" />
        <img src={TCA_SHORT_LOGO} alt="TCA Logo" className="absolute bottom-2 right-56 h-12 w-auto object-contain opacity-80" />
      </div>

      <Dialog open={errorModal.open} onOpenChange={(open) => setErrorModal({ ...errorModal, open })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-destructive flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              Required Information Missing
            </DialogTitle>
            <DialogDescription className="text-base pt-2">{errorModal.message}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setErrorModal({ open: false, message: '' })} className="bg-accent hover:bg-accent/90 text-accent-foreground">
              OK, I'll fix it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PowerOfAttorney;
